import Main from "modules/layout/dashboard/main"
import { useEventId } from ".."
import { useEvent } from "../queries"
import {
  useApplyTransition,
  useExecutionMonitors,
  useMonitors,
} from "./queries"
import { useTranslation } from "react-i18next"
import { useNavigate, useSearchParams } from "react-router-dom"
import { Button, Input, Typography } from "antd"
import AssignMonitor from "./assign"
import { usePopup } from "utils/use-popup"
import Loader from "components/loader"
import MonitorItem from "components/monitor-item"
import { useMemo } from "react"
import TransitionModal from "./transitionModal"
import styles from "./styles.module.scss"
import { IMonitor } from "./types"
import PhoneModal from "./phoneModal"
import { ReactComponent as MonitorIcon } from "assets/icons/monitor.svg"
import { ReactComponent as AddMonitorIcon } from "assets/icons/add-members.svg"

import UnassignMonitorModal from "./unassing"
import useWindowDimensions from "utils/use-window-dimension"

const { Title } = Typography

const MONITOR_PARAM = "monitor"

const Monitors: React.FC = () => {
  const { t } = useTranslation()
  const { width } = useWindowDimensions()
  let [searchParams, setSearchParams] = useSearchParams()
  const navigate = useNavigate()
  const assignMonitorDrawer = usePopup()
  const unassignMonitorModal = usePopup<string>()
  const transitionModal = usePopup<{ transition: string; monitorId: string }>()
  const monitorPhoneModal = usePopup<IMonitor["person"]>()
  const eventId = useEventId()
  const event = useEvent(eventId!)

  const monitors = useMonitors(eventId!, {
    enabled: event.data?.status === "approved",
  })
  const monitorsExecution = useExecutionMonitors(eventId!, {
    enabled: event.data?.status === "execution",
  })

  const displayedMonitors = useMemo(
    () => (event.data?.status === "execution" ? monitorsExecution : monitors),
    [monitors, monitorsExecution, event.data?.status]
  )

  const applyTransition = useApplyTransition(eventId!)

  const isLoading = event.isLoading || displayedMonitors.isLoading

  return (
    <>
      <Main.PageHeader
        onButtonClick={() => navigate("../..")}
        title={<Title level={4}>{event.data?.name}</Title>}
        rightContent={
          event.data?.status === "execution" && (
            <Button
              size="large"
              type="primary"
              className={styles.addMonitorButton}
              loading={event.isLoading}
              onClick={assignMonitorDrawer.open}
            >
              <AddMonitorIcon style={width! > 520 ? { marginRight: 10 } : {}} />
              {width! > 520 && t("monitors.assign_monitor")}
            </Button>
          )
        }
      />
      <div className={styles.searchInputContainer}>
        <Input
          className={styles.searchInput}
          size="large"
          placeholder={t("global.search")}
          value={searchParams.get(MONITOR_PARAM) ?? ""}
          onChange={(e) => {
            e.target.value
              ? searchParams.set(MONITOR_PARAM, e.target.value)
              : searchParams.delete(MONITOR_PARAM)
            setSearchParams(searchParams, { replace: true })
          }}
        />
      </div>
      <div className={styles.listContainer}>
        {isLoading ? (
          <Loader />
        ) : displayedMonitors.data?.length! > 0 ? (
          displayedMonitors.data
            ?.filter((monitor) =>
              monitor.person.name
                .toLowerCase()
                .includes(searchParams.get(MONITOR_PARAM)?.toLowerCase() ?? "")
            )
            .map((item) => (
              <MonitorItem
                key={item.id}
                unassignMonitorModal={unassignMonitorModal.open}
                openPhoneModal={monitorPhoneModal.open}
                transitionModal={transitionModal}
                applyTransition={applyTransition}
                isLoading={applyTransition.isLoading}
                item={item}
              />
            ))
        ) : (
          <div className={styles.emptyRenderer}>
            <MonitorIcon height={35} />
            <Title level={4}>
              {t("global.no_x_found", {
                entity: t("monitors.monitor"),
                context: "male",
                count: 1,
              })}
            </Title>
          </div>
        )}
      </div>
      <AssignMonitor
        {...assignMonitorDrawer}
        open={assignMonitorDrawer.isOpen}
      />
      <UnassignMonitorModal
        {...unassignMonitorModal}
        open={unassignMonitorModal.isOpen}
      />
      <TransitionModal
        {...transitionModal}
        applyTransition={applyTransition}
        open={transitionModal.isOpen}
      />
      <PhoneModal {...monitorPhoneModal} open={monitorPhoneModal.isOpen} />
    </>
  )
}

export default Monitors
